import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Tag, MapPin, CheckCircle2, XCircle, X } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, formatINR } from "../lib/api";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";

export default function Checkout() {
  const { items, subtotal, clearCart, hamperLinePrice, hasHamper } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState({ razorpay_enabled: false, razorpay_key_id: "" });
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState();

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount_amount, ...}

  // Pincode delivery check for hampers
  const [pincodeStatus, setPincodeStatus] = useState("idle"); // idle | checking | ok | not_ok

  const [form, setForm] = useState({
    recipient_name: "",
    recipient_phone: "",
    delivery_address: "",
    city: "",
    pincode: "",
    delivery_slot: "",
    message_on_card: "",
    payment_mode: "razorpay",
  });

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setConfig(data);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      toast.info("Please sign in to place your order");
      nav("/login?next=/checkout");
    }
  }, [user, nav]);

  useEffect(() => {
    if (items.length === 0) nav("/cart");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pincodes list — used to detect if the entered pincode is a "Hassan" city
  // (Hassan is our same-day base; other pincodes only support future-date orders).
  const [allPincodes, setAllPincodes] = useState([]);
  useEffect(() => {
    api.get("/pincodes").then(({ data }) => setAllPincodes(data)).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pincodeMeta = useMemo(
    () => allPincodes.find((p) => p.pincode === form.pincode.trim()),
    [allPincodes, form.pincode],
  );
  const isHassanPincode = (pincodeMeta?.city || "").toLowerCase().includes("hassan");

  // Slots — 30-min buckets from 9:00 AM to 10:00 PM IST.
  // If today (Hassan same-day), start = ceil(now IST + 3h) to the next 30-min mark.
  const IST_OFFSET_MIN = 330;
  // Shift any Date by +5:30 so getUTC* reads its IST wall-clock components.
  const toIST = (d) => new Date(d.getTime() + IST_OFFSET_MIN * 60000);
  const nowIST = () => toIST(new Date());

  const isSameISTDate = (a, b) => {
    if (!a || !b) return false;
    const A = toIST(a);
    const B = toIST(b);
    return (
      A.getUTCFullYear() === B.getUTCFullYear() &&
      A.getUTCMonth() === B.getUTCMonth() &&
      A.getUTCDate() === B.getUTCDate()
    );
  };

  const isToday = date ? isSameISTDate(date, new Date()) : false;

  const fmtSlot = (h, m) => {
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2, "0")} ${suffix}`;
  };

  const slotOptions = useMemo(() => {
    let startH = 9, startM = 0;
    if (isToday) {
      const t = nowIST();
      t.setMinutes(t.getMinutes() + 180); // now + 3h
      // round UP to next 30-min mark
      const mm = t.getUTCMinutes();
      if (mm === 0) { /* keep */ }
      else if (mm <= 30) t.setUTCMinutes(30, 0, 0);
      else { t.setUTCHours(t.getUTCHours() + 1); t.setUTCMinutes(0, 0, 0); }
      startH = t.getUTCHours();
      startM = t.getUTCMinutes();
    }
    const out = [];
    let h = startH, m = startM;
    while (h < 22 || (h === 22 && m === 0)) {
      out.push(fmtSlot(h, m));
      m += 30;
      if (m >= 60) { h += 1; m -= 60; }
    }
    return out;
  }, [isToday, form.pincode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first slot when options change (or clear if not in list)
  useEffect(() => {
    if (!slotOptions.length) { setField("delivery_slot", ""); return; }
    if (!slotOptions.includes(form.delivery_slot)) setField("delivery_slot", slotOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotOptions.join("|")]);

  // Pincode check — runs when pincode has 6 digits AND cart has hampers
  useEffect(() => {
    const pin = form.pincode.trim();
    if (!hasHamper) {
      setPincodeStatus("idle");
      return;
    }
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      setPincodeStatus("idle");
      return;
    }
    setPincodeStatus("checking");
    api
      .get(`/pincodes/check`, { params: { pincode: pin } })
      .then(({ data }) => setPincodeStatus(data.hamper_delivery ? "ok" : "not_ok"))
      .catch(() => setPincodeStatus("idle"));
  }, [form.pincode, hasHamper]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    try {
      const { data } = await api.get("/coupons/validate", { params: { code, subtotal } });
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applied — saved ${formatINR(data.discount_amount)}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const discountAmount = appliedCoupon?.discount_amount || 0;
  const totalAfterDiscount = Math.max(0, subtotal - discountAmount);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const placeOrder = async () => {
    if (!date) return toast.error("Please pick a delivery date");
    if (!form.recipient_name || !form.recipient_phone || !form.delivery_address || !form.city || !form.pincode) {
      return toast.error("Please fill all delivery details");
    }
    if (hasHamper && pincodeStatus === "not_ok") {
      return toast.error("Bobo Balloon hampers aren't delivered to this pincode yet. Please remove hampers or use a different pincode.");
    }
    if (!config.razorpay_enabled) {
      return toast.error("Online payments are temporarily unavailable. Please contact us on WhatsApp at +91 78994 81956 to place your order.");
    }
    setLoading(true);
    try {
      const basePayload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          addons: (it.addons || []).map((a) => ({ addon_id: a.addon_id, quantity: a.quantity })),
          selected_toy_ids: it.selected_toy_ids || [],
        })),
        delivery_date: date.toISOString(),
        delivery_slot: form.delivery_slot,
        recipient_name: form.recipient_name,
        recipient_phone: form.recipient_phone,
        delivery_address: form.delivery_address,
        city: form.city,
        pincode: form.pincode,
        message_on_card: form.message_on_card,
        payment_mode: form.payment_mode,
        coupon_code: appliedCoupon?.code || null,
      };

      if (form.payment_mode === "razorpay") {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Failed to load Razorpay");
        const { data: rzOrder } = await api.post("/payments/razorpay/order", { amount: totalAfterDiscount });
        await new Promise((resolve, reject) => {
          const rz = new window.Razorpay({
            key: rzOrder.key_id,
            amount: rzOrder.amount,
            currency: rzOrder.currency,
            order_id: rzOrder.id,
            name: "BalloonBites",
            description: "Order",
            prefill: { name: user.name, email: user.email, contact: user.phone },
            theme: { color: "#d08c8c" },
            handler: async (res) => {
              try {
                const { data: order } = await api.post("/orders", {
                  ...basePayload,
                  razorpay_order_id: res.razorpay_order_id,
                  razorpay_payment_id: res.razorpay_payment_id,
                  razorpay_signature: res.razorpay_signature,
                });
                clearCart();
                toast.success("Order placed successfully!");
                nav(`/order-success/${order.id}`);
                resolve();
              } catch (e) {
                reject(e);
              }
            },
            modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
          });
          rz.open();
        });
      } else {
        const { data: order } = await api.post("/orders", basePayload);
        clearCart();
        toast.success("Order placed successfully!");
        nav(`/order-success/${order.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10" data-testid="checkout-page">
        <p className="label-tiny mb-3">Checkout</p>
        <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl">Almost there.</h1>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            {/* Delivery details */}
            <div className="rounded-3xl border border-[#e8e2da] bg-white p-8">
              <h2 className="font-serif text-2xl">Delivery details</h2>
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="rname">Recipient name</Label>
                  <Input id="rname" data-testid="checkout-recipient-name" value={form.recipient_name}
                    onChange={(e) => setField("recipient_name", e.target.value)} className="mt-2 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="rphone">Recipient phone</Label>
                  <Input id="rphone" data-testid="checkout-recipient-phone" value={form.recipient_phone}
                    onChange={(e) => setField("recipient_phone", e.target.value)} className="mt-2 rounded-xl" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="raddr">Delivery address</Label>
                  <Textarea id="raddr" data-testid="checkout-address" value={form.delivery_address}
                    onChange={(e) => setField("delivery_address", e.target.value)} className="mt-2 rounded-xl" rows={3} />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" data-testid="checkout-city" value={form.city}
                    onChange={(e) => setField("city", e.target.value)} className="mt-2 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input id="pincode" data-testid="checkout-pincode" value={form.pincode}
                    onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-2 rounded-xl" />
                  {hasHamper && pincodeStatus !== "idle" && (
                    <div
                      data-testid="pincode-status"
                      className={`mt-2 flex items-center gap-2 text-xs ${
                        pincodeStatus === "ok" ? "text-emerald-700" : pincodeStatus === "not_ok" ? "text-red-700" : "text-[#7a7571]"
                      }`}
                    >
                      {pincodeStatus === "checking" && (<><Loader2 className="h-3 w-3 animate-spin" /> Checking hamper delivery…</>)}
                      {pincodeStatus === "ok" && (<><CheckCircle2 className="h-3.5 w-3.5" /> Hampers deliverable at this pincode</>)}
                      {pincodeStatus === "not_ok" && (<><XCircle className="h-3.5 w-3.5" /> Hampers aren&apos;t shipped to this pincode yet. Chocolates ship anywhere.</>)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="rounded-3xl border border-[#e8e2da] bg-white p-8">
              <h2 className="font-serif text-2xl">Schedule delivery</h2>
              <p className="mt-2 text-sm text-[#7a7571]">Pick when you&apos;d like your order delivered.</p>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Delivery date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="checkout-date-picker"
                        className="mt-2 flex w-full items-center justify-between rounded-xl border border-[#e8e2da] bg-white px-4 py-2.5 text-left text-sm"
                      >
                        {date ? format(date, "PPP") : "Pick a date"}
                        <CalendarIcon className="h-4 w-4 text-[#7a7571]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => {
                          // Disallow past dates only; today is always allowed
                          // (same-day slots start at ceil(now IST + 3h)).
                          const todayIST = nowIST();
                          const dIST = toIST(d);
                          // Compare by IST wall-clock day
                          const dY = dIST.getUTCFullYear(), dM = dIST.getUTCMonth(), dD = dIST.getUTCDate();
                          const tY = todayIST.getUTCFullYear(), tM = todayIST.getUTCMonth(), tD = todayIST.getUTCDate();
                          if (dY < tY) return true;
                          if (dY === tY && dM < tM) return true;
                          if (dY === tY && dM === tM && dD < tD) return true;
                          return false;
                        }}
                        data-testid="checkout-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Time slot (IST)</Label>
                  {slotOptions.length === 0 ? (
                    <div className="mt-2 rounded-xl border border-[#e8e2da] bg-[#faf1ee] p-3 text-xs text-[#7a7571]" data-testid="slot-cutoff-notice">
                      No same-day slots left. Please pick a future date.
                    </div>
                  ) : (
                    <div className="mt-2 flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-xl border border-[#e8e2da] bg-white p-2" data-testid="slot-picker">
                      {slotOptions.map((s) => {
                        const on = form.delivery_slot === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`slot-${s.replace(/\s+/g, "-")}`}
                            onClick={() => setField("delivery_slot", s)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                              on ? "border-[#d08c8c] bg-[#d08c8c] text-white" : "border-[#e8e2da] bg-white text-[#2a2928] hover:border-[#d08c8c]"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#7a7571]">
                    {isToday
                      ? "Same-day delivery — slots start 3 hours from now (IST)."
                      : "Slots run 9:00 AM to 10:00 PM IST for future dates."}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="msg">Message on card (optional)</Label>
                  <Textarea id="msg" data-testid="checkout-message"
                    placeholder="Happy Birthday, love always..."
                    value={form.message_on_card}
                    onChange={(e) => setField("message_on_card", e.target.value)}
                    className="mt-2 rounded-xl" rows={3} />
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="rounded-3xl border border-[#e8e2da] bg-white p-8">
              <h2 className="font-serif text-2xl">Payment</h2>
              {config.razorpay_enabled ? (
                <div
                  data-testid="checkout-pay-razorpay"
                  className="mt-6 flex items-start gap-3 rounded-2xl border-2 border-[#d08c8c] bg-[#faf1ee] p-4"
                >
                  <div className="mt-1 h-3 w-3 rounded-full bg-[#d08c8c]" />
                  <div>
                    <div className="font-medium">Pay online (Razorpay)</div>
                    <div className="text-xs text-[#7a7571]">UPI, cards, netbanking, wallets — secure checkout.</div>
                  </div>
                </div>
              ) : (
                <div
                  data-testid="checkout-pay-unavailable"
                  className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  Online payments are temporarily unavailable. Please try again later or contact us on WhatsApp at +91 78994 81956 to place your order.
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="h-fit rounded-3xl border border-[#e8e2da] bg-white p-8" data-testid="checkout-summary">
            <h3 className="font-serif text-xl">Your order</h3>
            <div className="mt-4 space-y-3">
              {items.map((it) => (
                <div key={it.line_id} className="text-sm">
                  <div className="flex justify-between">
                    <span className="pr-2 text-[#2a2928]">{it.name} × {it.quantity}</span>
                    <span className="text-[#2a2928]">{formatINR(hamperLinePrice(it) * it.quantity)}</span>
                  </div>
                  {it.addons && it.addons.length > 0 && (
                    <div className="mt-1 space-y-0.5 pl-3 text-xs text-[#7a7571]">
                      {it.addons.map((a) => (
                        <div key={a.addon_id} className="flex justify-between">
                          <span>+ {a.name} × {a.quantity}</span>
                          <span>{formatINR(a.price * a.quantity)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Coupon */}
            <div className="mt-6 border-t border-[#e8e2da] pt-4" data-testid="coupon-block">
              <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#7a7571]">
                <Tag className="h-3 w-3" /> Coupon
              </div>
              {appliedCoupon ? (
                <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="coupon-applied">
                  <div>
                    <div className="font-semibold">{appliedCoupon.code}</div>
                    <div className="text-xs text-emerald-700">
                      –{formatINR(appliedCoupon.discount_amount)} off
                      {appliedCoupon.source === "mylocalgo" && " · via MyLocalGo"}
                    </div>
                  </div>
                  <button
                    onClick={removeCoupon}
                    data-testid="coupon-remove"
                    className="rounded-full p-1 text-emerald-700 hover:bg-emerald-100"
                    aria-label="Remove coupon"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="coupon-input"
                    placeholder="MyLocalGo code…"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    className="rounded-xl uppercase"
                  />
                  <Button
                    data-testid="coupon-apply"
                    onClick={applyCoupon}
                    disabled={!couponInput.trim() || couponLoading}
                    variant="outline"
                    className="rounded-full"
                  >
                    {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
              <a
                href={process.env.REACT_APP_MYLOCALGO_URL || "https://mylocalgo.in"}
                target="_blank"
                rel="noreferrer"
                data-testid="coupon-mylocalgo-link"
                className="mt-2 inline-block text-xs text-[#d08c8c] hover:underline"
              >
                Don&apos;t have a code? Get one on MyLocalGo →
              </a>
              <p className="mt-1 text-[11px] text-[#a08a7a]" data-testid="coupon-scope-note">
                MyLocalGo offer applies to Hampers &amp; Gifts only — Customised Chocolates are enquiry-only.
              </p>
            </div>

            <div className="mt-6 space-y-2 border-t border-[#e8e2da] pt-4 text-sm">
              <div className="flex justify-between"><span className="text-[#7a7571]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="checkout-discount">
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>−{formatINR(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-[#7a7571]">Delivery</span><span>Free</span></div>
              <div className="mt-3 flex justify-between border-t border-[#e8e2da] pt-3 font-serif text-lg">
                <span>Total</span><span data-testid="checkout-total">{formatINR(totalAfterDiscount)}</span>
              </div>
            </div>

            {hasHamper && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#faf9f6] p-3 text-xs text-[#7a7571]" data-testid="hamper-shipping-note">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#d08c8c]" />
                <span>Balloon hampers ship to selected pincodes only. Chocolates ship all-India.</span>
              </div>
            )}

            <Button
              data-testid="checkout-place-order"
              onClick={placeOrder}
              disabled={loading || (hasHamper && pincodeStatus === "not_ok")}
              className="mt-6 w-full rounded-full bg-[#d08c8c] py-6 text-white hover:bg-[#b87a7a]"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Place order
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

